import { ClientErrorSource } from "@appEnum/ClientErrorSource";
import type { ApiCallRecord } from "@utility/activityTracker";
import type IClientErrorApiCall from "./interfaces/IClientErrorApiCall";
import type IClientErrorReport from "./interfaces/IClientErrorReport";

export const CLIENT_ERROR_REPORT_ENDPOINT = "ClientError/report";

const MAX_API_CALLS = 10;
const MAX_REPORTS_PER_PAGE = 10;
const MAX_MESSAGE_LENGTH = 2000;
const MAX_STACK_LENGTH = 8000;
const MAX_ENDPOINT_LENGTH = 200;
const CROSS_ORIGIN_SCRIPT_ERROR = "Script error.";

/**
 * Extracts a message from an unknown error object.
 * @param error
 */
export const extractMessage = (error?: unknown): string | null => {
  if (!error) {
    return null;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

/**
 * Extracts a stack trace from an unknown error object.
 * @param error
 */
export const extractStack = (error?: unknown): string | null => {
  return error instanceof Error ? (error.stack ?? null) : null;
};

/**
 * Builds the report a customer site sends for an error: its message and stack, the page path, and the recent API
 * calls reduced to endpoint, method, status and duration. Request and response bodies never leave the page.
 * Returns null when there is nothing to report, as with the bare "Script error." a cross-origin script raises.
 * @param error
 * @param pagePath The page's path, without its query string or hash.
 * @param apiCalls The activity tracker's recent calls, oldest first.
 */
export const buildClientErrorReport = (
  error: unknown,
  pagePath: string,
  apiCalls: ApiCallRecord[]
): IClientErrorReport | null => {
  const message = extractMessage(error);
  const stack = extractStack(error);
  if ((message === null || message === CROSS_ORIGIN_SCRIPT_ERROR) && stack === null) {
    return null;
  }

  return {
    SourceId: ClientErrorSource.Portal,
    Message: message?.slice(0, MAX_MESSAGE_LENGTH) ?? null,
    Stack: stack?.slice(0, MAX_STACK_LENGTH) ?? null,
    PageUrl: pagePath,
    ApiCalls: apiCalls
      .filter((call) => call.endpoint !== CLIENT_ERROR_REPORT_ENDPOINT)
      .slice(-MAX_API_CALLS)
      .map(toReportApiCall),
  };
};

/**
 * Limits what one page load reports: each distinct error once, and at most `maxReports` in all, so an error
 * thrown in a loop, or by a failing report, cannot flood the server.
 * @param maxReports
 */
export const createClientErrorReportGate = (maxReports: number = MAX_REPORTS_PER_PAGE) => {
  const reported = new Set<string>();

  const shouldReport = (report: IClientErrorReport): boolean => {
    const key = `${report.Message ?? ""}\n${topStackFrame(report.Stack)}`;
    if (reported.size >= maxReports || reported.has(key)) {
      return false;
    }
    reported.add(key);
    return true;
  };

  return { shouldReport };
};

const toReportApiCall = (call: ApiCallRecord): IClientErrorApiCall => ({
  Endpoint: stripQueryAndHash(call.endpoint).slice(0, MAX_ENDPOINT_LENGTH),
  Method: call.method,
  Status: call.status ?? null,
  DurationMs: call.duration ?? null,
});

const stripQueryAndHash = (url: string): string => url.split(/[?#]/, 1)[0];

const topStackFrame = (stack: string | null): string =>
  (stack ?? "")
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.startsWith("at ") || line.includes("@")) ?? "";
